import React, { useCallback, useRef, useState } from 'react';
import Map, { Layer, MapRef, MapLayerMouseEvent, Marker, Source, ViewState, type LngLatLike } from 'react-map-gl';

import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { Box, Chip } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import { observer } from 'mobx-react-lite';

import { missionPlannerStore } from '../../stores/MissionPlannerStore';
import type { SimpleItem } from '../../types/Plan';
import { LocationSearch } from '../LocationSearch';
import 'mapbox-gl/dist/mapbox-gl.css';

const TOKEN = 'pk.eyJ1IjoianVsaWFub2VzIiwiYSI6ImNrOGNwbXVzbTA2bnMzZW95bmhvemIwbGkifQ.vHzrkhopow_IrtZm5yP5iA';

const PlannerMap: React.FC = () => {
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>({
    longitude: -121.99794686292485,
    latitude: 37.412695452262625,
    zoom: 16,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 }
  });
  const [searchedLocation, setSearchedLocation] = useState<LngLatLike | null>(null);

  const simpleMissionItems = missionPlannerStore.missionItems.filter(
    (item): item is SimpleItem => item.type === 'SimpleItem'
  );

  const takeoffItem = simpleMissionItems.find((item) => item.command === 22);

  const takeoffCircleSource: GeoJSON.FeatureCollection<GeoJSON.Point> = takeoffItem
    ? {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [takeoffItem.coordinate[1], takeoffItem.coordinate[0]]
            },
            properties: {}
          }
        ]
      }
    : { type: 'FeatureCollection', features: [] }; // Provide empty data instead of undefined

  const missionPathCoordinates = simpleMissionItems.map((item) => [item.coordinate[1], item.coordinate[0]]);

  const missionPathSource: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: missionPathCoordinates
        },
        properties: {}
      }
    ]
  };

  const onClick = useCallback((e: MapLayerMouseEvent) => {
    if (missionPlannerStore.mode !== 'Mission') return;

    const { lng, lat } = e.lngLat;
    const tool = missionPlannerStore.activeTool;

    if (tool === 'Takeoff') {
      const hasTakeoff = missionPlannerStore.missionItems.some(
        (item) => item.type === 'SimpleItem' && item.command === 22
      );
      if (hasTakeoff) {
        console.warn('A takeoff command already exists for this mission.');
        return;
      }
      missionPlannerStore.addMissionItem(
        {
          type: 'SimpleItem',
          command: 22, // MAV_CMD_NAV_TAKEOFF
          frame: 3, // FRAME_GLOBAL_REL_ALT
          autoContinue: true,
          doJumpId: missionPlannerStore.missionItems.length + 1,
          params: [0, 0, 0, 0, lat, lng, 50], // Param 5: Latitude, Param 6: Longitude, Param 7: Altitude
          coordinate: [lat, lng, 50]
        },
        0 // Add at the beginning of the mission
      );
      missionPlannerStore.selectItem(0);
    }

    if (tool === 'Waypoint') {
      missionPlannerStore.addMissionItem({
        type: 'SimpleItem',
        command: 16, // MAV_CMD_NAV_WAYPOINT
        frame: 3, // FRAME_GLOBAL_REL_ALT
        autoContinue: true,
        doJumpId: missionPlannerStore.missionItems.length + 1,
        params: [0, 0, 0, 0, lat, lng, 50], // Param 5: Latitude, Param 6: Longitude, Param 7: Altitude
        coordinate: [lat, lng, 50]
      });
      missionPlannerStore.selectItem(missionPlannerStore.missionItems.length - 1);
    }
  }, []);

  const handleLocationFound = (coords: LngLatLike) => {
    setSearchedLocation(coords);
    mapRef.current?.flyTo({ center: coords, zoom: 16 });
  };

  const handleClearLocation = () => {
    setSearchedLocation(null);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1, width: '30%' }}>
        <LocationSearch
          onLocationFound={handleLocationFound}
          onClear={handleClearLocation}
          proximity={{ longitude: viewState.longitude, latitude: viewState.latitude }}
        />
      </Box>
      <Map
        ref={mapRef}
        mapboxAccessToken={TOKEN}
        mapStyle='mapbox://styles/mapbox/satellite-streets-v12'
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onClick={onClick}
        style={{ width: '100%', height: '100%' }}
      >
        <Source id='mission-path' type='geojson' data={missionPathSource}>
          <Layer
            id='mission-path-layer'
            type='line'
            paint={{
              'line-color': '#FBC02D', // High-contrast yellow
              'line-width': 2,
              'line-opacity': 0.9
            }}
          />
        </Source>

        <Source id='takeoff-circle' type='geojson' data={takeoffCircleSource}>
          <Layer
            id='takeoff-circle-layer'
            type='circle'
            paint={{
              'circle-radius': 20,
              'circle-color': 'rgba(31, 122, 31, 0.5)', // Theme 'success' color in RGBA
              'circle-stroke-width': 1,
              'circle-stroke-color': 'rgba(19, 108, 19, 1)' // Opaque stroke for visibility
            }}
          />
        </Source>

        {missionPlannerStore.missionItems.map((item, idx) => {
          if (item.type !== 'SimpleItem') return null;

          // Render Takeoff Point
          if (item.command === 22) {
            return (
              <Marker
                key={`mission-${idx}`}
                longitude={item.coordinate[1]}
                latitude={item.coordinate[0]}
                anchor='center'
                onClick={() => missionPlannerStore.selectItem(idx)}
              >
                <FlightTakeoffIcon
                  sx={{
                    fontSize: 28,
                    color: missionPlannerStore.selectedIndex === idx ? theme.palette.success.solidBg : 'white'
                  }}
                />
              </Marker>
            );
          }

          // Render Waypoint
          if (item.command === 16) {
            return (
              <Marker
                key={`mission-${idx}`}
                longitude={item.coordinate[1]}
                latitude={item.coordinate[0]}
                anchor='center'
                onClick={() => missionPlannerStore.selectItem(idx)}
              >
                <Chip color={missionPlannerStore.selectedIndex === idx ? 'success' : 'primary'}>{idx + 1}</Chip>
              </Marker>
            );
          }

          return null;
        })}
        {searchedLocation && Array.isArray(searchedLocation) && (
          <Marker longitude={searchedLocation[0]} latitude={searchedLocation[1]} anchor='center' color='blue' />
        )}
      </Map>
    </Box>
  );
};

export default observer(PlannerMap);
